import { type KeyboardEvent, type PointerEvent, useRef, useState } from "react";
import type {
  HomeLayout,
  HomeWidget,
  HomeWidgetDescriptor,
} from "../../api/hub/home";
import {
  adjustWidget,
  type LayoutAction,
  placeWidget,
  sortedWidgets,
} from "./layout";
import { WidgetBody, widgetTitle } from "./Widget";
import { message } from "./data";
import { GridSelectionToolbar } from "./GridSelectionToolbar";
type Props = {
  layout: HomeLayout;
  descriptors: HomeWidgetDescriptor[];
  editing?: boolean;
  sharedOnly?: boolean;
  onChange?: (layout: HomeLayout) => void;
  onConfigure?: (widget: HomeWidget) => void;
};
export function HomeGrid(
  {
    layout,
    descriptors,
    editing = false,
    sharedOnly = false,
    onChange,
    onConfigure,
  }: Props,
) {
  const grid = useRef<HTMLDivElement>(null),
    resize = useRef<
      {
        id: string;
        startX: number;
        startY: number;
        w: number;
        h: number;
        cell: number;
      } | null
    >(null);
  const [selected, setSelected] = useState<string | null>(null),
    [error, setError] = useState("");
  const change = (fn: () => HomeLayout, id: string) => {
    try {
      onChange?.(fn());
      setSelected(id);
      setError("");
    } catch (e) {
      setError(message(e));
    }
  };
  const adjust = (id: string, action: LayoutAction) =>
    change(() => adjustWidget(layout, id, action, descriptors), id);
  const keyboard = (event: KeyboardEvent, widget: HomeWidget) => {
    if (!editing || !event.altKey || !event.key.startsWith("Arrow")) return;
    event.preventDefault();
    const action = event.shiftKey
      ? event.key === "ArrowLeft"
        ? "narrow"
        : event.key === "ArrowRight"
        ? "wide"
        : null
      : ({
        ArrowLeft: "left",
        ArrowRight: "right",
        ArrowUp: "up",
        ArrowDown: "down",
      } as Record<string, LayoutAction>)[event.key];
    if (action) adjust(widget.id, action);
  };
  const resizeStart = (
    event: PointerEvent<HTMLButtonElement>,
    widget: HomeWidget,
  ) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    resize.current = {
      id: widget.id,
      startX: event.clientX,
      startY: event.clientY,
      w: widget.w,
      h: widget.h,
      cell: ((grid.current?.getBoundingClientRect().width ?? 600) + 12) / 12,
    };
    setSelected(widget.id);
  };
  const resizeMove = (event: PointerEvent<HTMLButtonElement>) => {
    const start = resize.current;
    if (!start) return;
    const w = start.w + Math.round((event.clientX - start.startX) / start.cell),
      h = start.h + Math.round((event.clientY - start.startY) / 52);
    change(
      () => placeWidget(layout, start.id, { w, h }, descriptors),
      start.id,
    );
  };
  const activeWidget =
    layout.widgets.find((widget) => widget.id === selected) ??
      sortedWidgets(layout)[0];
  const remove = (id: string) =>
    onChange?.({
      ...layout,
      widgets: layout.widgets.filter((widget) => widget.id !== id),
    });
  return (
    <>
      {error && <p className="home-error" role="alert">{error}</p>}
      {editing && (
        <>
          <p className="home-edit-help">
            拖动标题把手；右下角调整大小。键盘 Alt + 方向键移动，Alt + Shift +
            左右调宽。
          </p>
          <GridSelectionToolbar
            layout={layout}
            descriptors={descriptors}
            selected={activeWidget}
            onSelect={setSelected}
            onAdjust={adjust}
            onConfigure={onConfigure}
            onRemove={remove}
            onKeyDown={keyboard}
          />
          <div className="home-grid-ruler" aria-hidden="true">
            {Array.from({ length: 12 }, (_, i) => <span key={i}>{i + 1}</span>)}
          </div>
        </>
      )}
      <div
        ref={grid}
        className={"home-grid " + (editing ? "is-editing" : "")}
        aria-label={editing ? "编辑个人布局" : "个人主页布局"}
        onDragOver={editing
          ? (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
          }
          : undefined}
        onDrop={editing
          ? (e) => {
            e.preventDefault();
            const id = e.dataTransfer.getData("text/lingzhi-widget");
            const widget = layout.widgets.find((item) => item.id === id),
              bounds = grid.current?.getBoundingClientRect();
            if (!widget || !bounds) return;
            const cell = (bounds.width + 12) / 12;
            change(() =>
              placeWidget(layout, id, {
                x: Math.floor((e.clientX - bounds.left) / cell),
                y: Math.round((e.clientY - bounds.top) / 52),
              }, descriptors), id);
          }
          : undefined}
      >
        {sortedWidgets(layout).map((widget) => {
          const descriptor = descriptors.find((item) =>
              item.type === widget.type
            ),
            title = widgetTitle(widget, descriptor);
          return (
            <section
              key={widget.id}
              className={"home-widget " + (editing ? "is-editing" : "")}
              style={{
                gridColumn: String(widget.x + 1) + " / span " + widget.w,
                gridRow: String(widget.y + 1) + " / span " + widget.h,
              }}
              tabIndex={editing ? 0 : undefined}
              data-widget-id={widget.id}
              data-selected={editing && activeWidget?.id === widget.id}
              aria-label={title +
                (editing
                  ? "，" + widget.w + " 列宽，" + widget.h + " 行高"
                  : "")}
              onFocus={() => editing && setSelected(widget.id)}
              onPointerDown={() => editing && setSelected(widget.id)}
              onKeyDown={(e) => keyboard(e, widget)}
            >
              <header className="home-widget-header">
                <div className="home-actions">
                  {editing && (
                    <button
                      className="home-drag-handle"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData(
                          "text/lingzhi-widget",
                          widget.id,
                        );
                        e.dataTransfer.effectAllowed = "move";
                        setSelected(widget.id);
                      }}
                      aria-label={"拖动" + title}
                    >
                      ⠿
                    </button>
                  )}
                  <h2>{title}</h2>
                </div>
              </header>
              <div className="home-widget-body">
                <WidgetBody
                  widget={widget}
                  descriptor={descriptor}
                  sharedOnly={sharedOnly}
                />
              </div>
              {editing && (
                <button
                  className="home-resize-handle"
                  aria-label={"拖动调整" + title + "大小"}
                  onPointerDown={(e) => resizeStart(e, widget)}
                  onPointerMove={resizeMove}
                  onPointerUp={() => {
                    resize.current = null;
                  }}
                  onPointerCancel={() => {
                    resize.current = null;
                  }}
                >
                  ↘
                </button>
              )}
            </section>
          );
        })}
        {!layout.widgets.length && (
          <section className="home-empty-layout">
            <h2>给工作桌放一个起点</h2>
            <p>添加一个今日焦点或任务部件，或者恢复角色默认。</p>
          </section>
        )}
      </div>
    </>
  );
}
